import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by
 * `<theme-drawer>`.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * The restore path can set [open] before this
     * module loads, so the drawer-open event may
     * already have fired.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Waits for the browser to apply the freshly
   * rendered cart drawer markup.
   */
  #waitForCartRender = async () => {
    await new Promise((resolve) => {
      requestAnimationFrame(resolve);
    });

    await new Promise((resolve) => {
      requestAnimationFrame(resolve);
    });
  };

  /**
   * Handles the theme drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /*
     * Close the cart drawer when the installments
     * CTA is clicked, avoiding overlapping dialogs.
     *
     * This is re-queried whenever the drawer opens
     * because cart rendering can replace the
     * payment-terms shadow root.
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const cta = this
          .querySelector('shopify-payment-terms')
          ?.shadowRoot?.querySelector(
            '#shopify-installments-cta'
          );

        cta?.addEventListener(
          'click',
          () => this.#themeDrawer?.close(),
          { once: true }
        );
      });
  };

  /**
   * Handles Shopify cart line updates.
   *
   * The cart is no longer opened immediately.
   * We first wait for the cart request to finish,
   * then allow cart-items-component to apply the
   * returned section HTML before opening the drawer.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    /*
     * If the update came from a modal quick-add,
     * wait until that modal closes so its focus
     * restoration completes first.
     */
    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );

    event.promise
      ?.then(async ({ detail }) => {
        /*
         * cart-items-component receives the same
         * resolved event and morphs the drawer.
         * Give that render two browser frames to
         * finish before opening the drawer.
         */
        await this.#waitForCartRender();

        if (
          shouldAutoOpen &&
          !detail?.didError
        ) {
          const openDrawer = () => {
            if (!this.#themeDrawer?.isOpen) {
              this.#themeDrawer?.open();
            }

            requestAnimationFrame(() => {
              this.#updateStickyState();
            });
          };

          if (sourceModal?.open) {
            sourceModal.addEventListener(
              'close',
              openDrawer,
              { once: true }
            );
          } else {
            openDrawer();
          }

          return;
        }

        this.#updateStickyState();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Updates whether the cart summary should remain
   * sticky inside the drawer.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    /*
     * Refs do not cross nested component boundaries,
     * so these elements are queried inside the dialog.
     */
    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}